// v2.1 finding #60 (WSL-08): pure arithmetic + shaping helpers that USED to
// live in `productionSamplingAggregateStorage.ts` alongside the localStorage
// sidecar. The sidecar retired at WSL-08 (server-backed `WeightSampleLog` is
// the single source of truth from WSL-01 onward), but the pure math survives
// because two callers still shape typed weight events into means and running
// aggregates:
//
// 1. `SamplingEventsPopover` — computes the header mean from server-fetched
//    events (post-WSL-05).
// 2. `productionLegacyDuplicateMergePlan` — computes the merged weight when
//    an operator fuses legacy duplicate `(lot, day)` rows into one, seeding
//    from each absorbed row's stored `weight` (post-WSL-08 the planner no
//    longer reads any storage — every event comes from the rows themselves).
//
// Nothing in this file touches localStorage, Dexie, or the network. Keeping
// it storage-free is the whole point of the WSL-08 split.

export type SamplingEvent = {
  recordedAt: string // ISO timestamp of the sampling event
  weightG: number // raw average-weight reading for this event (grams)
}

export type SamplingAggregate = {
  events: SamplingEvent[]
}

// Compute arithmetic mean of recorded weights (g). Returns null when there
// are no usable readings so the caller can fall back to the incoming raw
// value or show an em-dash.
export const computeMeanWeightG = (events: SamplingEvent[]): number | null => {
  if (!Array.isArray(events) || events.length === 0) return null
  let sum = 0
  let count = 0
  for (const event of events) {
    if (typeof event?.weightG !== 'number' || !Number.isFinite(event.weightG) || event.weightG <= 0) continue
    sum += event.weightG
    count += 1
  }
  if (count === 0) return null
  return sum / count
}

export const recordSamplingEvent = (
  byRowId: Record<string, SamplingAggregate>,
  rowId: string,
  event: SamplingEvent
): { nextByRowId: Record<string, SamplingAggregate>; aggregate: SamplingAggregate } => {
  const prev = byRowId[rowId]
  const prevEvents = Array.isArray(prev?.events) ? prev.events : []
  const aggregate: SamplingAggregate = {
    events: [...prevEvents, event]
  }
  return {
    nextByRowId: { ...byRowId, [rowId]: aggregate },
    aggregate
  }
}
